import { useEffect, useMemo } from 'react';
import parse from 'html-react-parser';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  MARK_VISIBILITY_GROUP_KEY,
  SEARCH_RESULT,
  VERSION_CONTENT_CONTAINER_ID,
  markIdUrlParam,
  searchKeywordUrlParam,
} from '../../shared/constants';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectActiveVersion,
  setActiveNote,
  setShowNotePopoverOpened,
  setVersionContentProcessing,
} from '../../redux/store/content/slice';
import { NoteVisibilityType } from '../../shared/enums';
import { VisibilityGroupString } from '../notes/types';
import { scrollMarkIntoView } from '../notes/functions';
import useMakeMarksClickable from './hooks.ts/useMakeMarksClickable';
import useMakeAnchorButtonsClickable from './hooks.ts/useMakeAnchorButtonsClickable';
import { useGetApiNotesByVersionIdQuery } from '../../redux/store/api/api';
import useGetVersionContent from './hooks.ts/useGetVersionContent';
import Loader from '../loader/Loader';
import VersionAlerts from './VersionAlerts';

function VersionContent() {
  const version = useAppSelector(selectActiveVersion);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { content, isProcessing } = useGetVersionContent();
  const [, setActiveMarkVisibilityGroup] =
    useSessionStorage<VisibilityGroupString | null>(
      MARK_VISIBILITY_GROUP_KEY,
      null,
    );
  const [searchParams, setSearchParams] = useSearchParams();
  const isSearchcontent = useMemo(
    () => searchParams.get(searchKeywordUrlParam) !== null,
    [searchParams],
  );
  const markIdFromBookmark: string | null = searchParams.get(markIdUrlParam);

  const { data: noteData } = useGetApiNotesByVersionIdQuery(
    version.id
      ? {
          versionId: version.id,
        }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  );

  useMakeMarksClickable();
  useMakeAnchorButtonsClickable();

  useEffect(() => {
    dispatch(setVersionContentProcessing(isProcessing));
  }, [isProcessing]);

  useEffect(() => {
    // Set focus on article title
    const articleTitleElement = document
      .getElementsByClassName('article-content')[0]
      .getElementsByClassName('card-title')[0]
      .getElementsByTagName('h1')[0];

    articleTitleElement.focus();
  }, [version]);

  useEffect(() => {
    const handler = (evt: MouseEvent | KeyboardEvent) => {
      const contentRoot = document.getElementById(VERSION_CONTENT_CONTAINER_ID);
      if (!contentRoot) return;

      const target = evt.target as Node | null;
      if (!target || !contentRoot.contains(target)) return;

      // Only left-clicks without modifiers, or Enter/Space keys
      const isMouse = evt instanceof MouseEvent;
      const isKeyboard = evt instanceof KeyboardEvent;
      if (isMouse) {
        if (evt.button !== 0) return;
        if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
      } else if (isKeyboard) {
        const { key } = evt;
        if (key !== 'Enter' && key !== ' ') return;
      } else {
        return;
      }

      // Find anchor via composedPath first, fallback to closest
      const path = (evt as any).composedPath?.() as EventTarget[] | undefined;
      const anchor =
        (path?.find((el) => el instanceof HTMLAnchorElement) as
          | HTMLAnchorElement
          | undefined) ||
        ((target as Element).closest?.(
          'a[href]',
        ) as HTMLAnchorElement | null) ||
        undefined;
      if (!anchor) return;

      const href = anchor.getAttribute('href') || '';
      // Intercept only internal article links
      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      const pathPrefixes = ['/article', '/category'];
      if (!pathPrefixes.some((prefix) => url.pathname.startsWith(prefix)))
        return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      // Prevent full page reload and route internally
      evt.preventDefault();
      evt.stopPropagation();
      (evt as any).stopImmediatePropagation?.();

      const to = `${url.pathname}${url.search}${url.hash}`;
      if (
        to !==
        `${window.location.pathname}${window.location.search}${window.location.hash}`
      ) {
        navigate(to);
      }
    };

    // Capture phase so we always see the event first
    document.addEventListener('click', handler, true);
    document.addEventListener('keydown', handler, true);
    return () => {
      document.removeEventListener('click', handler, true);
      document.removeEventListener('keydown', handler, true);
    };
  }, [navigate, content]);

  useEffect(() => {
    if (markIdFromBookmark) {
      const noteOfMarkIdInUrl = [
        ...(noteData?.resultObject?.userNotes || []),
        ...(noteData?.resultObject?.userGroupNotes || []),
        ...(noteData?.resultObject?.generalNotes || []),
      ].find((n) => n.mark?.id === markIdFromBookmark);

      if (noteOfMarkIdInUrl) {
        setActiveMarkVisibilityGroup(
          NoteVisibilityType[
            noteOfMarkIdInUrl.visibility || 0
          ] as VisibilityGroupString,
        );
        dispatch(setActiveNote(noteOfMarkIdInUrl));
        dispatch(setShowNotePopoverOpened(true));
        scrollMarkIntoView(noteOfMarkIdInUrl);
        searchParams.delete(markIdUrlParam);
        setSearchParams(searchParams);
      }
    }
  }, [markIdFromBookmark, content, noteData]);

  useEffect(() => {
    // Add spans with highlight border to search results
    if (!isSearchcontent) {
      return;
    }

    setTimeout(() => {
      const focusEl = document.getElementsByClassName(SEARCH_RESULT)[0];

      if (focusEl) {
        focusEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, [isSearchcontent]);

  useEffect(() => {
    // Ensure external links open in a new tab and are safe
    const root = document.getElementById(VERSION_CONTENT_CONTAINER_ID);
    if (!root) return;

    const anchors = Array.from(
      root.querySelectorAll<HTMLAnchorElement>('a[href]'),
    );
    anchors.forEach((a) => {
      const href = a.getAttribute('href') || '';
      try {
        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin) {
          a.setAttribute('target', '_blank');
          const rel = (a.getAttribute('rel') || '')
            .split(/\s+/)
            .filter(Boolean);
          if (!rel.includes('noopener')) rel.push('noopener');
          if (!rel.includes('noreferrer')) rel.push('noreferrer');
          if (!rel.includes('external')) rel.push('external');
          a.setAttribute('rel', rel.join(' '));
        }
      } catch {
        // ignore malformed hrefs
      }
    });
  }, [content]);

  return (
    <>
      {isProcessing && <Loader />}

      {!isProcessing && (
        <>
          <VersionAlerts />

          <div
            id={VERSION_CONTENT_CONTAINER_ID}
            className='mt-2 pt-1 version-content'>
            {parse(content)}
          </div>
        </>
      )}
    </>
  );
}

export default VersionContent;
